import { RefObject, useRef, useState } from "react";
import { NSARouterMakerResult } from "namirasoft-account-react";
import { NSBox, NSBoxDynamics, NSBoxEmail, NSBoxEntity, NSBoxPhone, NSBoxString, NSButtonBlue, NSColumn, NSPanel, NSRow, NSSection, NSSpace, NSSpaceSizeType, NSTabPage, NSLabel, NSCopy, NSTitle } from "namirasoft-site-react";
import { ContentTemplateFullRow, ContentTemplateRow } from "namirasoft-content-template";
import { ProviderRow as SMSProviderRow } from "namirasoft-sms-sender";
import { ProviderRow as EmailProviderRow } from "namirasoft-email-sender";
import { ProviderRow as TelegramProviderRow } from "namirasoft-telegram-sender";
import { ProviderRow as TeamsProviderRow } from "namirasoft-teams-sender";
import { ProviderRow as SlackProviderRow } from "namirasoft-slack-sender";
import { ContentTemplateLayout } from "../ContentTemplateLayout";
import { ContentTemplateProps } from "../ContentTemplateProps";
import { useParams } from "react-router-dom";
import { EnvService, FilterItem, FilterItemOperator } from "namirasoft-core";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseVariableSchema } from "namirasoft-schema";

interface MakePageProps extends NSARouterMakerResult, ContentTemplateProps { }

interface MakePageState
{
	content_template: ContentTemplateFullRow | null;
	content: string | null;
	version: number;
}

function MakePage(props: MakePageProps)
{
	let NSBoxDynamics_Parameters = useRef<NSBoxDynamics>(null);
	const NSBoxEntity_Content_Template = useRef<NSBoxEntity<ContentTemplateRow>>(null);
	const NSBoxEntity_SMS = useRef<NSBoxEntity<SMSProviderRow>>(null);
	const NSBoxEntity_Email = useRef<NSBoxEntity<EmailProviderRow>>(null);
	const NSBoxPhone_Phone = useRef<NSBoxPhone>(null);
	const NSBoxEmail_Email = useRef<NSBoxEmail>(null);
	const NSBoxString_Title = useRef<NSBoxString>(null);
	const NSBoxEntity_Telegram = useRef<NSBoxEntity<TelegramProviderRow>>(null);
	const NSBoxString_Telegram_ChatID = useRef<NSBoxString>(null);
	const NSBoxString_Telegram_MessageThreadID = useRef<NSBoxString>(null);
	const NSBoxEntity_Teams = useRef<NSBoxEntity<TeamsProviderRow>>(null);
	const NSBoxString_Teams_TeamID = useRef<NSBoxString>(null);
	const NSBoxString_Teams_ChannelID = useRef<NSBoxString>(null);
	const NSBoxEntity_Slack = useRef<NSBoxEntity<SlackProviderRow>>(null);
	const NSBoxString_Slack_Channel = useRef<NSBoxString>(null);

	const id_applied = useRef<boolean>(false);

	let { id } = useParams();

	let [state, setState] = useState<MakePageState>({
		content_template: null,
		content: null,
		version: 0,
	});

	async function loadContentTemplate(value: string)
	{
		try
		{
			let content_template = await props.servers.content_template.content_template.Get(value);
			setState(prevState => ({ ...prevState, content_template, content: null, version: prevState.version + 1 }));
		} catch (error)
		{
			props.notifier.onError(error as Error);
		}
	}

	async function onSelectContent(e: NSBoxEntity<ContentTemplateRow>)
	{
		let value = e.getValueOne();
		if (value)
			await loadContentTemplate(value);
	}

	function onLoadedContent()
	{
		if (id && !id_applied.current)
		{
			id_applied.current = true;
			NSBoxEntity_Content_Template.current?.setValue(id);
			return;
		}
		// Reload icon: re-fetch the selected template so parameter changes show up
		let value = NSBoxEntity_Content_Template.current?.getValueOne(false);
		if (value)
			loadContentTemplate(value);
	}

	function getParameters()
	{
		return NSBoxDynamics_Parameters.current?.getValues();
	}

	async function onMake(onFinished: () => void)
	{
		try
		{
			let parameters = getParameters();
			let value = NSBoxEntity_Content_Template.current?.getValueOne();
			if (value)
			{
				let res = await props.servers.content_template.content_template.Make(value, { parameters });
				setState(prevState => ({ ...prevState, content: res }));
			}
		} catch (error)
		{
			props.notifier.onError(error as Error);
		}
		onFinished();
	}

	async function onSendEmail(onFinished: () => void)
	{
		try
		{
			let parameters = getParameters();
			let content_template_id = NSBoxEntity_Content_Template.current?.getValueOne();
			let provider_id: string = NSBoxEntity_Email.current?.getValueOne() ?? "";
			let email: string = NSBoxEmail_Email.current?.getValue() ?? "";
			let title: string = NSBoxString_Title.current?.getValue() ?? "";
			if (content_template_id)
			{
				let res = await props.servers.content_template.content_template.SendEmail(content_template_id, provider_id, { email, title, parameters });
				props.notifier.onSuccess("An email was sent successfully.");

				let namirasoft = new NamirasoftMap(props.account.token_manager, props.notifier.onError);
				let filters = [new FilterItem(namirasoft.email_sender.tables.sent, namirasoft.email_sender.tables.sent.columns.id, false, FilterItemOperator.all.equals, res.id)];
				let REACT_APP_BASE_URL_EMAIL_SENDER_CONSOLE = new EnvService("REACT_APP_BASE_URL_EMAIL_SENDER_CONSOLE", true).getString();
				let link = props.url.getLink(REACT_APP_BASE_URL_EMAIL_SENDER_CONSOLE + '/sent/list', { filters: FilterItem.encode(filters) });
				window.open(link, "_blank");
			}
		} catch (error)
		{
			props.notifier.onError(error as Error);
		}
		onFinished();
	}

	async function onSendSMS(onFinished: () => void)
	{
		try
		{
			let parameters = getParameters();
			let content_template_id = NSBoxEntity_Content_Template.current?.getValueOne();
			let provider_id = NSBoxEntity_SMS.current?.getValueOne() ?? "";
			let phone: string = NSBoxPhone_Phone.current?.getValue() ?? "";
			if (content_template_id)
			{
				let res = await props.servers.content_template.content_template.SendSMS(content_template_id, provider_id, { phone, parameters });
				props.notifier.onSuccess("An SMS was sent successfully.");

				let namirasoft = new NamirasoftMap(props.account.token_manager, props.notifier.onError);
				let filters = [new FilterItem(namirasoft.sms_sender.tables.sent, namirasoft.sms_sender.tables.sent.columns.id, false, FilterItemOperator.all.equals, res.id)];
				let REACT_APP_BASE_URL_SMS_SENDER_CONSOLE = new EnvService("REACT_APP_BASE_URL_SMS_SENDER_CONSOLE", true).getString();
				let link = props.url.getLink(REACT_APP_BASE_URL_SMS_SENDER_CONSOLE + '/sent/list', { filters: FilterItem.encode(filters) });
				window.open(link, "_blank");
			}
		} catch (error)
		{
			props.notifier.onError(error as Error);
		}
		onFinished();
	}

	function getNullValue(ref: RefObject<NSBoxString>): string | null
	{
		let value = ref.current?.getValue() ?? "";
		return value ? value : null;
	}

	async function onSendTelegram(onFinished: () => void)
	{
		try
		{
			let parameters = getParameters();
			let content_template_id = NSBoxEntity_Content_Template.current?.getValueOne();
			let provider_id = NSBoxEntity_Telegram.current?.getValueOne() ?? "";
			let chat_id = getNullValue(NSBoxString_Telegram_ChatID);
			let message_thread_id = getNullValue(NSBoxString_Telegram_MessageThreadID);
			if (content_template_id)
			{
				let res = await props.servers.content_template.content_template.SendTelegram(content_template_id, provider_id, { chat_id, message_thread_id, parameters });
				props.notifier.onSuccess("A Telegram message was sent successfully.");

				let namirasoft = new NamirasoftMap(props.account.token_manager, props.notifier.onError);
				let filters = [new FilterItem(namirasoft.telegram_sender.tables.sent, namirasoft.telegram_sender.tables.sent.columns.id, false, FilterItemOperator.all.equals, res.id)];
				let REACT_APP_BASE_URL_TELEGRAM_SENDER_CONSOLE = new EnvService("REACT_APP_BASE_URL_TELEGRAM_SENDER_CONSOLE", true).getString();
				let link = props.url.getLink(REACT_APP_BASE_URL_TELEGRAM_SENDER_CONSOLE + '/sent/list', { filters: FilterItem.encode(filters) });
				window.open(link, "_blank");
			}
		} catch (error)
		{
			props.notifier.onError(error as Error);
		}
		onFinished();
	}

	async function onSendTeams(onFinished: () => void)
	{
		try
		{
			let parameters = getParameters();
			let content_template_id = NSBoxEntity_Content_Template.current?.getValueOne();
			let provider_id = NSBoxEntity_Teams.current?.getValueOne() ?? "";
			let team_id = getNullValue(NSBoxString_Teams_TeamID);
			let channel_id = getNullValue(NSBoxString_Teams_ChannelID);
			if (content_template_id)
			{
				let res = await props.servers.content_template.content_template.SendTeams(content_template_id, provider_id, { team_id, channel_id, parameters });
				props.notifier.onSuccess("A Teams message was sent successfully.");

				let namirasoft = new NamirasoftMap(props.account.token_manager, props.notifier.onError);
				let filters = [new FilterItem(namirasoft.teams_sender.tables.sent, namirasoft.teams_sender.tables.sent.columns.id, false, FilterItemOperator.all.equals, res.id)];
				let REACT_APP_BASE_URL_TEAMS_SENDER_CONSOLE = new EnvService("REACT_APP_BASE_URL_TEAMS_SENDER_CONSOLE", true).getString();
				let link = props.url.getLink(REACT_APP_BASE_URL_TEAMS_SENDER_CONSOLE + '/sent/list', { filters: FilterItem.encode(filters) });
				window.open(link, "_blank");
			}
		} catch (error)
		{
			props.notifier.onError(error as Error);
		}
		onFinished();
	}

	async function onSendSlack(onFinished: () => void)
	{
		try
		{
			let parameters = getParameters();
			let content_template_id = NSBoxEntity_Content_Template.current?.getValueOne();
			let provider_id = NSBoxEntity_Slack.current?.getValueOne() ?? "";
			let channel = getNullValue(NSBoxString_Slack_Channel);
			if (content_template_id)
			{
				let res = await props.servers.content_template.content_template.SendSlack(content_template_id, provider_id, { channel, parameters });
				props.notifier.onSuccess("A Slack message was sent successfully.");

				let namirasoft = new NamirasoftMap(props.account.token_manager, props.notifier.onError);
				let filters = [new FilterItem(namirasoft.slack_sender.tables.sent, namirasoft.slack_sender.tables.sent.columns.id, false, FilterItemOperator.all.equals, res.id)];
				let REACT_APP_BASE_URL_SLACK_SENDER_CONSOLE = new EnvService("REACT_APP_BASE_URL_SLACK_SENDER_CONSOLE", true).getString();
				let link = props.url.getLink(REACT_APP_BASE_URL_SLACK_SENDER_CONSOLE + '/sent/list', { filters: FilterItem.encode(filters) });
				window.open(link, "_blank");
			}
		} catch (error)
		{
			props.notifier.onError(error as Error);
		}
		onFinished();
	}

	let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

	let parameters = state.content_template?.parameters?.filter(p => p) as BaseVariableSchema[] ?? [];

	const parameters_info: any = { info: { link: "https://namirasoft.com/content-template/console-guide/send/#field-parameters" } };

	return (
		<ContentTemplateLayout  {...props}
			action={{
				title: `Make & Send`,
				description: "Generate and send personalized messages using your content templates.",
				menus: {}
			}}
		>
			<NSSection>
				<NSPanel>
					<NSBoxEntity
						ref={NSBoxEntity_Content_Template}
						required
						title="Content Template"
						info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-content-template" }}
						table={namirasoft.content_template.tables.content_template}
						getValue={item => item.id}
						getTitle={item => item.name}
						getSort={() => null}
						onChanged={onSelectContent}
						multiple={false}
						onLoaded={onLoadedContent}
						style={{ width: NSBox.width.double }}
					/>
					{
						state.content_template &&
						<NSColumn>
							<NSRow>
								<NSBoxDynamics
									key={state.version}
									name="parameters"
									ref={NSBoxDynamics_Parameters}
									variables={parameters}
									{...parameters_info}
								/>
							</NSRow>
						</NSColumn>
					}
				</NSPanel>
				<NSSpace size={NSSpaceSizeType.MEDUIM} />
				<NSButtonBlue
					title="Make"
					onClick={{ action: onMake, showLoading: true }}
				/>
				<NSSpace size={NSSpaceSizeType.MEDUIM} />
				{
					state.content && <>
						<NSTitle
							title="Output:"
						/>
						<NSCopy>
							<NSLabel title={state.content} />
						</NSCopy>
						<NSSpace size={NSSpaceSizeType.MEDUIM} />
						<NSTabPage
							style={{ width: "max-content" }}
							container={{ style: { width: NSBox.width.double } }}
							tabs={[
								{
									title: "Email",
									clicked: true,
									style: { width: "100%" },
									getContent: () =>
									{
										return (
											<>
												<NSColumn>
													<NSBoxEntity<EmailProviderRow>
														ref={NSBoxEntity_Email}
														required
														title="Email Sender"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-email-sender" }}
														table={namirasoft.email_sender.tables.provider}
														getValue={item => item.id}
														getTitle={item => item.name}
														getSort={() => null}
														multiple={false}
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxString
														ref={NSBoxString_Title}
														required
														title="Title"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-title" }}
														placeholder="Title"
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxEmail
														ref={NSBoxEmail_Email}
														required
														title="Email"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-email" }}
														style={{ width: NSBox.width.double }}
													/>
												</NSColumn>
												<NSSpace size={NSSpaceSizeType.NORMAL} />
												<NSRow>
													<NSButtonBlue
														title="Send"
														onClick={{ action: onSendEmail, showLoading: true }}
													/>
												</NSRow>
											</>
										)
									}
								},
								{
									title: "SMS",
									clicked: false,
									getContent: () =>
									{
										return (
											<>
												<NSColumn>
													<NSBoxEntity<SMSProviderRow>
														ref={NSBoxEntity_SMS}
														required
														title="SMS Sender"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-sms-sender" }}
														table={namirasoft.sms_sender.tables.provider}
														getValue={item => item.id}
														getTitle={item => item.name}
														getSort={() => null}
														multiple={false}
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxPhone
														ref={NSBoxPhone_Phone}
														required
														title="Phone"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-phone" }}
														style={{ width: NSBox.width.double }}
													/>
												</NSColumn>
												<NSSpace size={NSSpaceSizeType.NORMAL} />
												<NSRow>
													<NSButtonBlue
														title="Send"
														onClick={{ action: onSendSMS, showLoading: true }}
													/>
												</NSRow>
											</>
										)
									}
								},
								{
									title: "Telegram",
									clicked: false,
									getContent: () =>
									{
										return (
											<>
												<NSColumn>
													<NSBoxEntity<TelegramProviderRow>
														ref={NSBoxEntity_Telegram}
														required
														title="Telegram Sender"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-telegram-sender" }}
														table={namirasoft.telegram_sender.tables.provider}
														getValue={item => item.id}
														getTitle={item => item.name}
														getSort={() => null}
														multiple={false}
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxString
														ref={NSBoxString_Telegram_ChatID}
														required={false}
														title="Chat ID"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-chat-id" }}
														placeholder="Chat ID"
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxString
														ref={NSBoxString_Telegram_MessageThreadID}
														required={false}
														title="Message Thread ID"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-message-thread-id" }}
														placeholder="Message Thread ID"
														style={{ width: NSBox.width.double }}
													/>
												</NSColumn>
												<NSSpace size={NSSpaceSizeType.NORMAL} />
												<NSRow>
													<NSButtonBlue
														title="Send"
														onClick={{ action: onSendTelegram, showLoading: true }}
													/>
												</NSRow>
											</>
										)
									}
								},
								{
									title: "Teams",
									clicked: false,
									getContent: () =>
									{
										return (
											<>
												<NSColumn>
													<NSBoxEntity<TeamsProviderRow>
														ref={NSBoxEntity_Teams}
														required
														title="Teams Sender"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-teams-sender" }}
														table={namirasoft.teams_sender.tables.provider}
														getValue={item => item.id}
														getTitle={item => item.name}
														getSort={() => null}
														multiple={false}
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxString
														ref={NSBoxString_Teams_TeamID}
														required={false}
														title="Team ID"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-team-id" }}
														placeholder="Team ID"
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxString
														ref={NSBoxString_Teams_ChannelID}
														required={false}
														title="Channel ID"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-channel-id" }}
														placeholder="Channel ID"
														style={{ width: NSBox.width.double }}
													/>
												</NSColumn>
												<NSSpace size={NSSpaceSizeType.NORMAL} />
												<NSRow>
													<NSButtonBlue
														title="Send"
														onClick={{ action: onSendTeams, showLoading: true }}
													/>
												</NSRow>
											</>
										)
									}
								},
								{
									title: "Slack",
									clicked: false,
									getContent: () =>
									{
										return (
											<>
												<NSColumn>
													<NSBoxEntity<SlackProviderRow>
														ref={NSBoxEntity_Slack}
														required
														title="Slack Sender"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-slack-sender" }}
														table={namirasoft.slack_sender.tables.provider}
														getValue={item => item.id}
														getTitle={item => item.name}
														getSort={() => null}
														multiple={false}
														style={{ width: NSBox.width.double }}
													/>
													<NSBoxString
														ref={NSBoxString_Slack_Channel}
														required={false}
														title="Channel"
														info={{ link: "https://namirasoft.com/content-template/console-guide/send/#field-channel" }}
														placeholder="Channel"
														style={{ width: NSBox.width.double }}
													/>
												</NSColumn>
												<NSSpace size={NSSpaceSizeType.NORMAL} />
												<NSRow>
													<NSButtonBlue
														title="Send"
														onClick={{ action: onSendSlack, showLoading: true }}
													/>
												</NSRow>
											</>
										)
									}
								},
							]}
						/>
					</>
				}
			</NSSection>
		</ContentTemplateLayout >
	);
}

export default MakePage;